// src/screens/superadmin/support/Support.jsx
import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col } from "react-bootstrap";
import {
  FaSearch,
  FaTicketAlt,
  FaCheckCircle,
  FaClock,
  FaTimes,
  FaUser,
  FaEnvelope,
  FaCalendarAlt,
  FaExclamationCircle,
  FaComments,
  FaPaperPlane,
  FaPaperclip,
} from "react-icons/fa";
import Swal from "sweetalert2";
import axios from "axios";
import Button from "../../../components/general/button/Button";
import Pagination from "../../../components/general/pagination/Pagination";
import ActionDropdown from "../../../components/admin/dropdown/action/ActionDropdown";
import StatCard from "../../../components/admin/statcard/StatCard";
import "../../../assets/css/admin/help/HelpAndSupport.css";
import AdminLayout from "../../../layouts/AdminLayout";
import MyContext from "../../../context/MyContext";
import getEcho from "../../../components/general/echo/Echo";

const HelpAndSupport = () => {
  const navigate = useNavigate();
  const [conversationMessages, setConversationMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const { baseUrl, authToken } = useContext(MyContext);
  const [tickets, setTickets] = useState([]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [chatMessage, setChatMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [attachedFile, setAttachedFile] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  // Resolve Ticket modal — admin bhi (seller/deliveryman ki tarah) note
  // + refund_due de kar resolve karta hai, taake direct-to-admin
  // refund/billing tickets ka loop poora ho
  const [showAdminResolveModal, setShowAdminResolveModal] = useState(false);
  const [adminResolveTicket, setAdminResolveTicket] = useState(null);
  const [adminResolutionNote, setAdminResolutionNote] = useState("");
  const [adminRefundDue, setAdminRefundDue] = useState(false);
  const [adminFaultParty, setAdminFaultParty] = useState("none");

  const openAdminResolveModal = (ticket) => {
    setAdminResolveTicket(ticket);
    setAdminResolutionNote("");
    setAdminRefundDue(false);
    setAdminFaultParty("none");
    setShowAdminResolveModal(true);
  };

  const submitAdminResolveTicket = () => {
    if (!adminResolutionNote.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Note required",
        text: "Please add a note explaining the resolution.",
      });
      return;
    }
    changeTicketStatus(
      adminResolveTicket,
      "resolved",
      adminResolutionNote,
      adminRefundDue,
      adminFaultParty,
    );
    setShowAdminResolveModal(false);
  };
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
    });
  }, [conversationMessages]);

  // ── Pusher — jab bhi customer/seller/deliveryman ticket mein reply
  //    kare, real-time yahan message aa jaye (bina refresh kiye) ──
  useEffect(() => {
    if (!conversationId || !authToken) return;

    const echo = getEcho(baseUrl, authToken);
    if (!echo) return;

    const channel = echo.private(`conversation.${conversationId}`);
    channel.listen(".new-message", (data) => {
      setConversationMessages((prev) => [...prev, data.message]);
    });

    return () => {
      echo.leave(`conversation.${conversationId}`);
    };
  }, [conversationId, authToken, baseUrl]);
  // Status options
  const statusOptions = [
    { value: "all", label: "All Status" },
    { value: "open", label: "Open" },
    { value: "in_progress", label: "In Progress" },
    { value: "resolved", label: "Resolved" },
    { value: "closed", label: "Closed" },
  ];

  // Priority options
  const priorityOptions = [
    { value: "all", label: "All Priority" },
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" },
    { value: "urgent", label: "Urgent" },
  ];

  // API calling — kmadmin_id ab bhejna zaroori nahi, backend
  // authenticated admin khud detect karta hai (auth('Kmadmin'))
  const viewAllTickets = () => {
    const formData = new FormData();

    axios
      .post(`${baseUrl}/admin/help/viewAllTickets`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        setLoading(false);
        console.log(response.data);
        if (response.data.hasOwnProperty("error")) {
          setErrorMessage(response.data.error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          setTickets(response.data.ticket);
          setLoading(false);
        }
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Support Tickets cannot load",
        });
      });
  };

  useEffect(() => {
    if (!authToken) {
      setLoading(false);
      navigate("/admin/login");
      return;
    }
    viewAllTickets();
  }, [authToken]);
  const fetchChatForTicket = (ticket) => {
    const formData = new FormData();
    formData.append("kmhelpandsupport_id", ticket.id);

    axios
      .post(`${baseUrl}/admin/chat/fetchChatForHelp`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        if (res.data.success) {
          setConversationMessages(res.data.chats);
          setConversationId(res.data.conversation_id);
        } else {
          setConversationMessages([]);
        }
      })
      .catch((err) => console.log(err));
  };
  // Stats calculation — UNCHANGED
  const totalTickets = tickets.length;
  const openTickets = tickets.filter((t) => t.status === "open").length;
  const inProgressTickets = tickets.filter(
    (t) => t.status === "in_progress",
  ).length;
  const resolvedTickets = tickets.filter((t) => t.status === "resolved").length;

  const statsData = [
    {
      icon: FaTicketAlt,
      iconBg: "linear-gradient(135deg, #e3f2fd, #bbdefb)",
      iconColor: "#2196f3",
      label: "Total Tickets",
      value: totalTickets.toString(),
      badge: "All",
    },
    {
      icon: FaClock,
      iconBg: "linear-gradient(135deg, #fff3e0, #ffe0b2)",
      iconColor: "#ff9800",
      label: "Open Tickets",
      value: openTickets.toString(),
      badge: "Pending",
    },
    {
      icon: FaExclamationCircle,
      iconBg: "linear-gradient(135deg, #ffebee, #ffcdd2)",
      iconColor: "#f44336",
      label: "In Progress",
      value: inProgressTickets.toString(),
      badge: "Active",
    },
    {
      icon: FaCheckCircle,
      iconBg: "linear-gradient(135deg, #e8f5f0, #d4ede3)",
      iconColor: "#0ac46c",
      label: "Resolved",
      value: resolvedTickets.toString(),
      badge: "Done",
    },
  ];

  const handleView = (ticket) => {
    setSelectedTicket(ticket);
    setShowViewModal(true);

    setChatMessage("");
    setAttachedFile(null);

    fetchChatForTicket(ticket);
  };

  // Send Message in Chat — UNCHANGED
  const handleSendMessage = () => {
    if (!chatMessage.trim() && !attachedFile) return;
    if (!conversationId) return;

    const formData = new FormData();
    formData.append("kmconversation_id", conversationId);
    formData.append("send_by", "admin");
    formData.append("message", chatMessage);

    if (attachedFile) {
      formData.append("image", attachedFile);
    }

    axios
      .post(`${baseUrl}/admin/chat/sendMessage`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        if (response.data.error) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          setChatMessage("");
          setAttachedFile(null);

          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }

          // ── FIX: pehle yahan fetchChatForTicket() dobara call hoti thi —
          //    ab Echo real-time se hi naya message (admin ka apna bhi)
          //    conversation channel se wapis aa jata hai. Dobara fetch
          //    karne se message duplicate ho jata (ek refetch se, ek
          //    Echo push se) ──
        }
      })
      .catch((err) => console.log(err));
  };
  // Handle file attachment — UNCHANGED
  const handleFileAttach = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10000000) {
        alert("File size should be less than 10MB");
        return;
      }
      setAttachedFile(file);
    }
  };

  // Remove attached file — UNCHANGED
  const handleRemoveFile = () => {
    setAttachedFile(null);
  };

  // Handle Enter key press — UNCHANGED
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const changeTicketStatus = (ticket, status, note, isRefundDue, faultParty) => {
    const formData = new FormData();
    formData.append("id", ticket?.id);
    formData.append("status", status);
    if (status === "resolved") {
      formData.append("resolution_note", note || "");
      formData.append("refund_due", isRefundDue ? 1 : 0);
      formData.append("fault_party", faultParty || "none");
    }
    axios
      .post(`${baseUrl}/admin/help/changeTicketStatus`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.success == true) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: " status successfully updated!",
          });
          viewAllTickets();
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  // Add Response — UNCHANGED
  const handleAddResponse = (ticketId) => {
    const response = window.prompt("Enter your response:");
    if (response) {
      setTickets(
        tickets.map((ticket) =>
          ticket.id === ticketId
            ? {
                ...ticket,
                response: response,
                status: "in_progress",
                updatedDate: new Date().toISOString().split("T")[0],
              }
            : ticket,
        ),
      );
      alert("Response added successfully!");
    }
  };

  // Filter tickets — UNCHANGED
  const filteredTickets = tickets.filter((ticket) => {
    const raisedBy =
      ticket.kmcustomer?.name || ticket.kmdeliveryman?.name || "";
    const against = ticket.kmseller?.shop_name || "Admin";
    const matchesSearch =
      String(ticket.id).includes(searchTerm) ||
      raisedBy.toLowerCase().includes(searchTerm.toLowerCase()) ||
      against.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (ticket.subject || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      filterStatus === "all" || ticket.status === filterStatus;
    const matchesPriority =
      filterPriority === "all" || (ticket.priority || "") === filterPriority;
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const getActions = (ticket) => {
    const actions = [
      {
        icon: "MdVisibility",
        label: "View Details",
        onClick: () => handleView(ticket),
      },
    ];

    if (!ticket.status || ticket.status === "pending") {
      actions.push({
        icon: "MdPlayArrow",
        label: "Mark as Open",
        onClick: () => changeTicketStatus(ticket, "open"),
      });
    }

    if (ticket.status === "open") {
      actions.push({
        icon: "MdPlayArrow",
        label: "Mark as Open",
        onClick: () => changeTicketStatus(ticket, "open"),
      });
      actions.push({
        icon: "MdCheckCircle",
        label: "Mark Resolved",
        onClick: () => openAdminResolveModal(ticket),
        className: "success",
      });
    }

    if (ticket.status === "in_progress") {
      actions.push({
        icon: "MdCheckCircle",
        label: "Mark Resolved",
        onClick: () => openAdminResolveModal(ticket),
        className: "success",
      });
    }

    if (ticket.status === "resolved") {
      actions.push({
        icon: "MdClose",
        label: "Close Ticket",
        onClick: () => changeTicketStatus(ticket, "closed"),
        className: "danger",
      });
    }

    return actions;
  };

  // Badge helpers — UNCHANGED
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "open":
        return "ksa-support-badge-open";
      case "in_progress":
        return "ksa-support-badge-progress";
      case "resolved":
        return "ksa-support-badge-resolved";
      case "closed":
        return "ksa-support-badge-closed";
      default:
        return "ksa-support-badge-default";
    }
  };

  const getPriorityBadgeClass = (priority) => {
    switch (priority) {
      case "low":
        return "ksa-support-priority-low";
      case "medium":
        return "ksa-support-priority-medium";
      case "high":
        return "ksa-support-priority-high";
      case "urgent":
        return "ksa-support-priority-urgent";
      default:
        return "ksa-support-priority-default";
    }
  };

  const getStatusLabel = (status) => {
    return (status || "pending")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  return (
    <AdminLayout>
      <div>
        {/* Page Header */}
        <div className="ksa-support-page-header">
          <div>
            <h1 className="ksa-layout-page-title">Help & Support</h1>
            <p className="ksa-layout-page-subtitle">
              Manage customer support tickets and queries.
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <Row className="g-4 mb-4 ksa-support-stats">
          {loading
            ? Array(4)
                .fill(0)
                .map((_, idx) => (
                  <Col key={idx} xs={12} sm={6} lg={3}>
                    <div className="ksa-stat-card ksa-skeleton-stat">
                      <div className="ksa-skeleton-content">
                        <div className="ksa-skeleton-line ksa-skeleton-label"></div>
                        <div className="ksa-skeleton-line ksa-skeleton-value"></div>
                      </div>
                      <div className="ksa-skeleton-icon"></div>
                    </div>
                  </Col>
                ))
            : statsData.map((stat, idx) => (
                <Col key={idx} xs={12} sm={6} lg={3}>
                  <StatCard {...stat} />
                </Col>
              ))}
        </Row>

        {/* Main Table Card */}
        <div className="ksa-card">
          {/* Filters and Search */}
          <div className="ksa-support-table-header">
            <div className="ksa-support-filters">
              <select
                className="ksa-support-filter-select"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>

              <select
                className="ksa-support-filter-select"
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
              >
                {priorityOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="ksa-support-search">
              <FaSearch size={16} />
              <input
                type="text"
                placeholder="Search tickets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Table */}
          <div className="ksa-support-table-wrapper">
            <table className="ksa-support-table">
              <thead>
                <tr>
                  <th>TICKET ID</th>
                  <th>ORDER ID</th>
                  <th>RAISED BY</th>
                  <th>AGAINST</th>
                  <th>SUBJECT</th>
                  <th>TYPE</th>
                  <th>STATUS</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {loading
                  ? Array(5)
                      .fill(0)
                      .map((_, idx) => (
                        <tr key={idx}>
                          <td>
                            <div
                              className="ksa-skeleton-line"
                              style={{ width: "120px" }}
                            ></div>
                          </td>
                          <td>
                            <div
                              className="ksa-skeleton-line"
                              style={{ width: "150px", marginBottom: "6px" }}
                            ></div>
                            <div
                              className="ksa-skeleton-line"
                              style={{ width: "100px" }}
                            ></div>
                          </td>
                          <td>
                            <div
                              className="ksa-skeleton-line"
                              style={{ width: "150px", marginBottom: "6px" }}
                            ></div>
                            <div
                              className="ksa-skeleton-line"
                              style={{ width: "100px" }}
                            ></div>
                          </td>
                          <td>
                            <div
                              className="ksa-skeleton-line"
                              style={{ width: "180px" }}
                            ></div>
                          </td>
                          <td>
                            <div
                              className="ksa-skeleton-line"
                              style={{ width: "80px" }}
                            ></div>
                          </td>
                          <td>
                            <div className="ksa-skeleton-badge"></div>
                          </td>
                          <td>
                            <div className="ksa-skeleton-actions"></div>
                          </td>
                        </tr>
                      ))
                  : filteredTickets
                      .slice(
                        (currentPage - 1) * itemsPerPage,
                        currentPage * itemsPerPage,
                      )
                      .map((ticket) => (
                        <tr key={ticket.id}>
                          <td className="ksa-support-ticket-id">{ticket.id}</td>
                          <td className="ksa-support-order">
                            {ticket.kmorder?.id || ticket.kmorder_id || "N/A"}
                          </td>
                          <td className="ksa-support-user">
                            <div>
                              <p className="ksa-support-user-name">
                                {ticket.kmcustomer?.name ||
                                  ticket.kmdeliveryman?.name ||
                                  ticket.kmseller?.shop_name ||
                                  "N/A"}
                              </p>

                              <p className="ksa-support-user-email">
                                {ticket.kmcustomer?.email ||
                                  ticket.kmdeliveryman?.email ||
                                  ticket.kmseller?.email ||
                                  "N/A"}
                              </p>
                            </div>
                          </td>
                          <td className="ksa-support-user">
                            <div>
                              <p className="ksa-support-shop-name">
                                {ticket.kmcustomer
                                  ? ticket.kmseller?.shop_name
                                  : ticket.kmdeliveryman
                                    ? ticket.kmcustomer?.name
                                    : ticket.kmseller
                                      ? ticket.kmadmin?.name
                                      : "Admin"}
                              </p>

                              <p className="ksa-support-shop-role">
                                {ticket.kmcustomer ? "Seller" : "Admin"}
                              </p>
                            </div>
                          </td>
                          <td className="ksa-support-subject">
                            {ticket.subject}
                          </td>
                          <td className="ksa-support-type">{ticket.type}</td>
                          <td>
                            <span
                              className={`ksa-support-status-badge ${getStatusBadgeClass(ticket.status)}`}
                            >
                              {getStatusLabel(ticket.status)}
                            </span>
                            {ticket.refund_due == 1 && (
                              <span className="ksa-support-status-badge ksa-support-status--refund">
                                ⚠ Refund
                              </span>
                            )}
                          </td>
                          <td>
                            <ActionDropdown actions={getActions(ticket)} />
                          </td>
                        </tr>
                      ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <Pagination
            totalItems={filteredTickets.length}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        </div>

        {/* View Ticket Details Modal */}
        {showViewModal && selectedTicket && (
          <div
            className="ksa-modal-overlay"
            onClick={() => setShowViewModal(false)}
          >
            <div
              className="ksa-modal ksa-modal-lg ksa-support-view-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="ksa-support-modal-header">
                <div>
                  <h3 className="ksa-support-modal-title">Ticket Details</h3>
                  <p className="ksa-support-modal-number">
                    {selectedTicket.ticketNumber}
                  </p>
                </div>
                <button
                  className="ksa-modal-close"
                  onClick={() => setShowViewModal(false)}
                >
                  <FaTimes size={18} />
                </button>
              </div>

              <div className="ksa-support-modal-body">
                <Row className="g-4">
                  {/* Left Column */}
                  <Col xs={12} md={4}>
                    <div className="ksa-support-info-card">
                      <h5>Ticket Information</h5>
                      <div className="ksa-support-info-item">
                        <FaUser />
                        <div>
                          <label>Raised By</label>
                          <p>
                            {selectedTicket.kmcustomer?.name ||
                              selectedTicket.kmdeliveryman?.name ||
                              selectedTicket.kmseller?.shop_name ||
                              "N/A"}
                          </p>
                        </div>
                      </div>
                      <div className="ksa-support-info-item">
                        <FaUser />
                        <div>
                          <label>Against</label>
                          <p>
                            {selectedTicket.kmseller?.shop_name ||
                              selectedTicket.kmadmin?.name ||
                              "Admin"}
                          </p>
                        </div>
                      </div>
                      <div className="ksa-support-info-item">
                        <FaEnvelope />
                        <div>
                          <label>Email</label>
                          <p>
                            {selectedTicket.kmcustomer?.email ||
                              selectedTicket.kmdeliveryman?.email ||
                              "N/A"}
                          </p>
                        </div>
                      </div>
                      <div className="ksa-support-info-item">
                        <FaCalendarAlt />
                        <div>
                          <label>Created</label>
                          <p>{selectedTicket.created_at?.split("T")[0]}</p>
                        </div>
                      </div>
                      <div className="ksa-support-info-item">
                        <FaCalendarAlt />
                        <div>
                          <label>Updated</label>
                          <p>{selectedTicket.updated_at?.split("T")[0]}</p>
                        </div>
                      </div>

                      {/* NAYA: customer ka claimed refund amount aur
                          evidence — pehle yeh database mein save ho rahe
                          the lekin kahin display nahi hote the */}
                      {selectedTicket.claimed_amount_difference && (
                        <div className="ksa-support-info-item">
                          <FaUser />
                          <div>
                            <label>Claimed Amount</label>
                            <p>
                              Rs. {selectedTicket.claimed_amount_difference}
                            </p>
                          </div>
                        </div>
                      )}
                      {selectedTicket.attachment_image && (
                        <div className="ksa-support-info-item">
                          <FaUser />
                          <div>
                            <label>Evidence</label>
                            <a
                              href={`${baseUrl}/storage/${selectedTicket.attachment_image}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <img
                                src={`${baseUrl}/storage/${selectedTicket.attachment_image}`}
                                alt="evidence"
                                style={{
                                  maxWidth: "100%",
                                  borderRadius: "8px",
                                  marginTop: "4px",
                                }}
                              />
                            </a>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="ksa-support-status-card">
                      <h5>Status</h5>
                      <div className="ksa-support-badges">
                        <span
                          className={`ksa-support-status-badge ${getStatusBadgeClass(selectedTicket.status)}`}
                        >
                          {getStatusLabel(selectedTicket.status)}
                        </span>
                        {selectedTicket.refund_due == 1 && (
                          <span className="ksa-support-status-badge ksa-support-status--refund">
                            ⚠ Refund Required
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Teacher ka kaha hua note — seller/deliveryman ne
                        resolve karte waqt likha ke kis ke haq mein
                        resolve hua hai, taake admin ko poori chat parhne
                        ki zaroorat na pade */}
                    {selectedTicket.resolution_note && (
                      <div className="ksa-support-info-card">
                        <h5>Resolution Note</h5>
                        <p style={{ whiteSpace: "pre-wrap" }}>
                          {selectedTicket.resolution_note}
                        </p>
                        <p style={{ fontSize: "12px", color: "#6c757d" }}>
                          — by{" "}
                          {selectedTicket.kmseller
                            ? "Seller"
                            : selectedTicket.kmdeliveryman
                              ? "Deliveryman"
                              : "N/A"}
                        </p>
                      </div>
                    )}
                  </Col>

                  {/* Right Column - Chat Section */}
                  <Col xs={12} md={8}>
                    <div className="ksa-support-chat-section">
                      <h5 className="ksa-support-chat-title">
                        <FaComments /> Conversation
                      </h5>

                      {/* Subject Banner */}
                      <div className="ksa-support-subject-banner">
                        <strong>Subject:</strong> {selectedTicket.subject}
                      </div>

                      {/* Chat Messages */}
                      <div className="ksa-support-chat-container">
                        {conversationMessages.map((msg) => (
                          <div
                            key={msg.id}
                            className={`ksa-support-chat-message ${
                              msg.send_by === "admin" ? "user" : "other"
                            }`}
                          >
                            <div className="ksa-support-chat-bubble">
                              {msg.send_by !== "admin" && (
                                <small
                                  style={{
                                    fontWeight: "600",
                                    color: "#666",
                                    display: "block",
                                    marginBottom: "5px",
                                  }}
                                >
                                  {msg.send_by === "seller"
                                    ? selectedTicket.kmseller?.shop_name ||
                                      "Seller"
                                    : msg.send_by === "customer"
                                      ? selectedTicket.kmcustomer?.name ||
                                        "Customer"
                                      : msg.send_by}
                                </small>
                              )}
                              {msg.image && (
                                <img
                                  src={`${baseUrl}/storage/${msg.image}`}
                                  alt=""
                                  style={{
                                    maxWidth: "200px",
                                    borderRadius: "8px",
                                    marginBottom: "5px",
                                  }}
                                />
                              )}

                              {msg.message && <p>{msg.message}</p>}

                              <span className="ksa-support-chat-time">
                                {new Date(msg.created_at).toLocaleTimeString(
                                  [],
                                  {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  },
                                )}
                              </span>
                            </div>
                          </div>
                        ))}

                        <div ref={messagesEndRef}></div>
                      </div>

                      {/* Chat Input */}
                      {selectedTicket.status !== "resolved" &&
                      selectedTicket.status !== "closed" ? (
                        <div className="ksa-support-chat-input-wrapper">
                          {attachedFile && (
                            <div className="ksa-support-attached-file">
                              <FaPaperclip />
                              <span>{attachedFile.name}</span>
                              <button
                                className="ksa-support-remove-file"
                                onClick={handleRemoveFile}
                              >
                                <FaTimes />
                              </button>
                            </div>
                          )}

                          <div className="ksa-support-chat-input-container">
                            <label className="ksa-support-attach-btn">
                              <FaPaperclip />
                              <input
                                ref={fileInputRef}
                                type="file"
                                style={{ display: "none" }}
                                onChange={handleFileAttach}
                                accept="image/*,.pdf,.doc,.docx"
                              />
                            </label>
                            <input
                              type="text"
                              className="ksa-support-chat-input"
                              placeholder="Type your message..."
                              value={chatMessage}
                              onChange={(e) => setChatMessage(e.target.value)}
                              onKeyPress={handleKeyPress}
                            />

                            <button
                              className="ksa-support-chat-send-btn"
                              onClick={handleSendMessage}
                              disabled={!chatMessage.trim() && !attachedFile}
                            >
                              <FaPaperPlane />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="ksa-support-ticket-resolved">
                          <FaCheckCircle /> Ticket{" "}
                          {selectedTicket.status === "resolved"
                            ? "Resolved"
                            : "Closed"}
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>
              </div>

              <div className="ksa-support-modal-footer">
                <Button
                  variant="secondary"
                  onClick={() => setShowViewModal(false)}
                >
                  Close
                </Button>
                {selectedTicket.status === "open" && (
                  <Button
                    variant="warning"
                    onClick={() =>
                      changeTicketStatus(selectedTicket, "in_progress")
                    }
                  >
                    Mark In Progress
                  </Button>
                )}
                {selectedTicket.status === "in_progress" && (
                  <Button
                    variant="primary"
                    onClick={() => openAdminResolveModal(selectedTicket)}
                  >
                    Mark Resolved
                  </Button>
                )}
                {selectedTicket.status === "resolved" && (
                  <Button
                    variant="danger"
                    onClick={() => changeTicketStatus(selectedTicket, "closed")}
                  >
                    Close Ticket
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Admin Resolve Ticket Modal — note + refund_due */}
      {showAdminResolveModal && (
        <div
          className="ksa-modal-overlay"
          onClick={() => setShowAdminResolveModal(false)}
        >
          <div className="ksa-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ksa-support-modal-header">
              <h3 className="ksa-support-modal-title">Resolve Ticket</h3>
              <button
                className="ksa-modal-close"
                onClick={() => setShowAdminResolveModal(false)}
              >
                <FaTimes />
              </button>
            </div>
            <div className="ksa-support-modal-body">
              <label>Resolution note</label>
              <textarea
                className="form-control"
                rows={4}
                placeholder="e.g. Refund approved — item was damaged in transit."
                value={adminResolutionNote}
                onChange={(e) => setAdminResolutionNote(e.target.value)}
              />
              <div style={{ marginTop: "12px" }}>
                <label>
                  <input
                    type="checkbox"
                    checked={adminRefundDue}
                    onChange={(e) => setAdminRefundDue(e.target.checked)}
                    style={{ marginRight: "8px" }}
                  />
                  Approve refund for customer (they'll be asked for bank
                  details)
                </label>
              </div>
              {adminRefundDue && (
                <div style={{ marginTop: "12px" }}>
                  <label>Fault deduction from</label>
                  <select
                    className="form-control"
                    value={adminFaultParty}
                    onChange={(e) => setAdminFaultParty(e.target.value)}
                  >
                    <option value="seller">Seller</option>
                    <option value="deliveryman">Deliveryman</option>
                    <option value="none">
                      None (goodwill — platform absorbs)
                    </option>
                  </select>
                </div>
              )}
            </div>
            <div className="ksa-support-modal-footer">
              <Button
                variant="secondary"
                onClick={() => setShowAdminResolveModal(false)}
              >
                Cancel
              </Button>
              <Button variant="success" onClick={submitAdminResolveTicket}>
                Confirm Resolve
              </Button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default HelpAndSupport;
